import { useMemo } from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import Chart, { useChart } from 'src/components/chart';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import { CHART_HEIGHT, CHART_INNER_HEIGHT, formatExploreLabels } from './constants';

interface ExploreChartProps {
  toggle: any;
  filterStore: any;
}

// Arrondi vers un palier "propre" pour borner les axes (repris de ChartMixed).
const roundUpToNiceInterval = (value: number): number => {
  if (value <= 0) return 0.5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  let nice;
  if (normalized <= 1.2) nice = 1.2;
  else if (normalized <= 1.5) nice = 1.5;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 3) nice = 3;
  else if (normalized <= 5) nice = 5;
  else if (normalized <= 7) nice = 7;
  else nice = 10;
  return nice * magnitude;
};

// Formate une valeur selon le `format` du catalogue KPI ("currency:2", "percentage:1", "number:0"…),
// même logique que useGetFormatter dans generic-data-grid.
const makeFormatter = (format: string | undefined, locale: string, currency?: string) => {
  const [formatter, nbDecimals] = (format ?? '').split(':');
  const decimals = parseInt(nbDecimals, 10) || 0;
  if (formatter === 'currency') {
    return (value = 0) =>
      Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: value > 1000 ? 'compact' : 'standard',
      }).format(value ?? 0);
  }
  if (formatter === 'percentage') {
    return (value = 0) => `${(value ?? 0).toFixed(decimals)}%`;
  }
  if (formatter === 'number' || formatter === 'float') {
    return (value = 0) => (value ?? 0).toLocaleString(locale, { maximumFractionDigits: decimals });
  }
  return (value: number) => `${value}`;
};

const isPercentFormat = (format: string | undefined) => (format ?? '').split(':')[0] === 'percentage';

export default function ExploreChart({ toggle, filterStore }: ExploreChartProps) {
  const { currentLang } = useLocales();
  const locale = currentLang.value;
  const workspace: any = useCurrentWorkspace();
  const currency = workspace?.currency;

  const loading = toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData;

  // 1re métrique active → barres (axe gauche) ; les suivantes → ligne (axe droit).
  const series = useMemo(() => {
    const ts = toggle.statsData?.time_series_data ?? [];
    return toggle.kpiToggle
      .map((kpi: string, idx: number) => {
        const stat = toggle.stats.find((s: any) => s.key === kpi);
        if (!stat) return null;
        const percent = isPercentFormat(stat.format);
        const data = ts.map((item: any) => {
          const v = item[kpi] || 0;
          return percent ? Math.min(v, 100) : v;
        });
        return {
          name: stat.title,
          key: kpi,
          type: idx === 0 ? 'bar' : 'line',
          data,
          // KPI catalog color (recolored to the 2026 Figma palette in metrics.csv).
          color: stat.color,
          format: stat.format as string | undefined,
        };
      })
      .filter(Boolean) as Array<{ name: string; key: string; type: string; data: number[]; color: string; format?: string }>;
  }, [toggle.kpiToggle, toggle.stats, toggle.statsData]);

  const labels = formatExploreLabels(
    toggle.statsData?.time_series_data ?? [],
    filterStore.timestep,
    locale
  );

  const maxOf = (group: typeof series) => {
    const vals = group.flatMap((s) => s.data).filter((v) => v != null && Number.isFinite(v));
    const max = vals.length ? Math.max(...vals) : 0;
    return max <= 0 ? 0.5 : roundUpToNiceInterval(max * 1.3);
  };

  // 4 échelles Y distinctes (axes seulement, labels masqués) selon la catégorie métrique :
  // - traffic : trafic + brand searches (nombres)
  // - ctr     : pourcentages, borné à 100 %
  // - cost    : coûts (devise, ex. Ad Cost)
  // - cpc     : CPC (devise, valeurs généralement plus petites que le coût)
  // Chaque groupe a sa propre échelle → toutes les séries restent lisibles. On ne déclare
  // un axe que pour les groupes réellement présents (sinon ApexCharts plante sur un axe vide).
  type AxisKey = 'traffic' | 'ctr' | 'cost' | 'cpc';
  const COST_KEYS = new Set(['ad_cost', 'search_ad_cost', 'shopping_ad_cost', 'pmax_ad_cost']);
  const axisOf = (s: { key: string; format?: string }): AxisKey => {
    if (isPercentFormat(s.format)) return 'ctr';
    if ((s.format ?? '').split(':')[0] === 'currency') return COST_KEYS.has(s.key) ? 'cost' : 'cpc';
    return 'traffic';
  };

  // traffic & cost à gauche, ctr & cpc à droite (labels masqués → position sans impact visuel).
  const AXIS_ORDER: { key: AxisKey; opposite: boolean }[] = [
    { key: 'traffic', opposite: false },
    { key: 'cost', opposite: false },
    { key: 'ctr', opposite: true },
    { key: 'cpc', opposite: true },
  ];

  const yaxis = AXIS_ORDER.map(({ key, opposite }) => ({
    key,
    opposite,
    group: series.filter((s) => axisOf(s) === key),
  }))
    .filter(({ group }) => group.length > 0)
    .map(({ key, opposite, group }) => {
      const fmt = makeFormatter(group[0]?.format, locale, currency);
      return {
        seriesName: group.map((s) => s.name),
        min: 0,
        max: key === 'ctr' ? 100 : maxOf(group),
        show: true,
        opposite,
        labels: {
          show: true,
          formatter: (value: number) => fmt(value),
          style: { colors: '#919EAB', fontSize: '11px', fontFamily: 'Public Sans, sans-serif' },
        },
      };
    });

  // Formatters par série (ordre = series), pour le tooltip.
  const seriesFormatters = series.map((s) => makeFormatter(s.format, locale, currency));
  const colors = series.map((s) => s.color);

  const chartOptions = useChart({
    chart: { stacked: false, height: CHART_INNER_HEIGHT },
    plotOptions: {
      bar: { columnWidth: '65%', borderRadius: 4, borderRadiusApplication: 'end' },
    },
    dataLabels: { enabled: false },
    fill: { colors },
    colors,
    stroke: { width: series.map((s) => (s.type === 'bar' ? 0 : 1.4)) },
    labels,
    markers: { size: 0 },
    legend: { show: false },
    xaxis: { labels: { offsetY: 4 } },
    grid: { padding: { bottom: 8 } },
    // 4 échelles Y max (traffic / ctr / cost / cpc), une seule par groupe présent. Cf. calcul ci-dessus.
    yaxis,
    tooltip: {
      shared: true,
      intersect: false,
      custom({ series: s, dataPointIndex, w }: any) {
        if (!s || !w.globals.seriesNames || !w.globals.colors) return '';
        const date = labels[dataPointIndex] || '';
        const rows = s
          .map((serie: any, i: number) => {
            const value = serie[dataPointIndex];
            const formatted = seriesFormatters[i] ? seriesFormatters[i](value) : value;
            return `
              <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
                <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${w.globals.colors[i]};flex-shrink:0;"></span>
                <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                  <span>${w.globals.seriesNames[i]}</span>
                  <span>${formatted ?? ''}</span>
                </div>
              </div>`;
          })
          .join('');
        return `
          <div class="cb-clean-tooltip" style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 10px 0 rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
            <div style="padding:12px 12px 0 12px;">
              <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
            </div>
            <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">${rows}</div>
          </div>`;
      },
    },
  });

  return (
    <Paper
      sx={{
        backgroundColor: 'common.white',
        boxShadow:
          '0px 0px 2px 0px rgba(145,158,171,0.20), 0px 12px 24px -4px rgba(145,158,171,0.12)',
        border: '1px solid #eaeaea',
        height: `${CHART_HEIGHT}px`,
        borderRadius: '16px',
        marginTop: 2,
      }}
    >
      {loading ? (
        <Skeleton
          width="100%"
          height="100%"
          variant="rectangular"
          animation="wave"
          style={{ borderRadius: 16 }}
        />
      ) : (
        <div style={{ padding: '24px' }}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            {series.map((s) => (
              <Stack key={s.name} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 14, color: '#637381', whiteSpace: 'nowrap' }}>
                  {s.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Chart
            dir="ltr"
            type="line"
            series={series}
            options={chartOptions}
            height={CHART_INNER_HEIGHT}
          />
        </div>
      )}
    </Paper>
  );
}
